/**
 * 시스템 관리 (Phase 4-1) — admin only.
 *
 * 탭 2개:
 *   1. 계정 관리   — 사용자 목록 + 추가 + 수정 + 비번 재설정 (는 별도 모달)
 *   2. 메뉴 권한 설정 — 정적 매트릭스 (현재 정책 안내)
 *
 * 진입: 사이드바 ⚙ 시스템 관리. admin 외에는 접근 시 안내 화면.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as adminApi from '../lib/admin-api';
import { useAuth } from '../lib/auth-context';
import { ApprovalAdminTab } from '../components/ApprovalAdminTab';
import type { AdminUserRow, UserRole } from '../lib/types';
import { UserFormModal } from '../components/UserFormModal';
import { ResetPasswordModal } from '../components/ResetPasswordModal';
import { DataManagementTab, CalendarCleanupCard } from '../components/DataManagementTab';
// 네이버웍스 봇 테스트 메시지 — 어느 봇으로 나가는지 확인 (v2.157.0)
import { BotTestCard } from '../components/BotTestCard';
import { StockPhotoNasCard } from '../components/StockPhotoNasCard';
import { DeliveryOrphanCard } from '../components/DeliveryOrphanCard';
import { DeliveryMissingCard } from '../components/DeliveryMissingCard';
import { LastUpdateBadge } from '../components/LastUpdateBadge';
import { PageTabs } from '../components/ui/PageTabs';
import { LoadingLines } from '../components/ui/Skeleton';

// 거래처 관리는 별도 메뉴(PartnerCompanyPage, admin+mgmt)로 분리됨.
// [변경 이력] 탭은 독립 메뉴(/audit-log)와 완전 중복이라 제거 (2026-07-19 메뉴 재구성).
// [연동 도구] = 네이버웍스 캘린더 정리 등 외부 연동 유지보수 (데이터 관리에서 분리 승격).
type SysTab = 'accounts' | 'permissions' | 'approval' | 'data' | 'integrations' | 'integrity' | 'backup';

const ROLE_BADGE: Record<UserRole, string> = {
  admin: 'stage-badge stage-issue',
  mgmt: 'stage-badge stage-deliver',
  sales: 'stage-badge stage-order',
  viewer: 'stage-badge stage-quote',
};

function fmtDateTime(iso: string | null): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yy}-${mm}-${dd} ${hh}:${mi}`;
  } catch {
    return iso;
  }
}

export function SystemPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<SysTab>('accounts');

  if (user?.role !== 'admin') {
    return (
      <div className="p-8">
        <div className="bg-amber-50 border border-amber-200 rounded p-8 text-center">
          <div className="text-4xl mb-2">🔒</div>
          <div className="font-semibold text-amber-900 mb-1">접근 권한 없음</div>
          <div className="text-sm text-amber-700">
            시스템 관리는 관리자(admin) 권한이 있는 사용자만 접근할 수 있습니다.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="ui-page-title">시스템 관리</h1>
      <p className="ui-page-sub">
        계정·권한 안내·데이터 운영·정합성·백업 — 변경 이력은{' '}
        <span className="text-slate-600 font-medium">[📋 변경 이력]</span> 메뉴에서.
      </p>
      <LastUpdateBadge className="mb-3 mt-1" />

      <PageTabs
        tabs={[
          { key: 'accounts', label: '계정 관리' },
          { key: 'permissions', label: '메뉴 권한 안내' },
          { key: 'approval', label: '결재선 관리' },
          { key: 'data', label: '데이터 관리' },
          { key: 'integrations', label: '연동 도구' },
          { key: 'integrity', label: '정합성 점검' },
          { key: 'backup', label: '백업 / 복원' },
        ]}
        value={tab}
        onChange={setTab}
        ariaLabel="시스템 관리 보기"
      />

      {tab === 'accounts' && <AccountsTab />}
      {tab === 'permissions' && <PermissionsTab />}
      {tab === 'approval' && <ApprovalAdminTab />}
      {tab === 'data' && <DataManagementTab />}
      {tab === 'integrations' && (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            외부 서비스 연동 유지보수 도구 — 네이버웍스 봇·캘린더 등.
          </p>
          <BotTestCard />
          {/* 📷 재고 실사 사진 폴더(NAS) 쓰기 점검 (v2.224.0) */}
          <StockPhotoNasCard />
          {/* 📅 남은 납품 일정 — 어느 영업건도 안 가리키는 웍스 일정 (v2.277.0) */}
          <DeliveryOrphanCard />
          {/* 📅 빠진 납품 일정 — 예정일은 있는데 웍스 일정이 없는 영업건 (v2.285.0) */}
          <DeliveryMissingCard />
          <CalendarCleanupCard />
        </div>
      )}
      {tab === 'integrity' && <IntegrityTab />}
      {tab === 'backup' && <BackupRestoreTab />}
    </div>
  );
}

// 탭6 — 백업 / 복원
//   ⚠ 2026-07-30: 예전에 있던 [백업 파일 내려받기]·[이 백업으로 복원] 두 버튼을 제거했다.
//     둘 다 실제로는 동작하지 않는데 화면에는 "이 파일로 서버를 부활시킬 수 있다"고 적혀 있어
//     '거짓 안심'을 주고 있었다. 데이터가 커져(평문 3.3GB) 브라우저로 오가는 방식 자체가
//     성립하지 않고, 복원 파일 형식도 자동 백업(NAS)과 달랐다.
//     → 지금은 '자동 백업이 잘 되고 있는가' 를 보여주고, 복원은 서버 명령으로 안내한다.
function BackupRestoreTab() {
  return (
    <div className="space-y-6 max-w-3xl">
      {/* 자동 백업 현황 — 이 탭에서 가장 먼저 봐야 하는 정보 */}
      <AutoBackupCard />

      {/* 백업에 무엇이 들어 있나 */}
      <div className="bg-white border border-slate-200 rounded p-5">
        <h3 className="ui-subsection-title mb-1">📦 백업에 담기는 것</h3>
        <p className="text-sm text-slate-500 mb-3">
          매일 새벽 1시에 NAS(포털 서버와 분리된 장비)로 한 폴더씩 저장됩니다.
        </p>
        {/* 좁은 화면에서 표가 카드 밖으로 삐져나오지 않게 — 표 안에서만 가로 스크롤 */}
        <div className="overflow-x-auto">
        <table className="w-full text-xs border border-slate-200 rounded">
          <tbody>
            {[
              ['모든 데이터', '영업·매입·재고·유지보수 등 전부'],
              ['첨부서류·제품사진·PDF', '데이터 안에 함께 들어 있어 같이 복원됩니다'],
              ['비밀정보', '접속 비밀번호 · 세션키 · 2단계인증 암호키 · 웍스 개인키'],
              ['서버 설정', '웹서버·서비스 설정'],
              ['프로그램 전체', '인터넷이 안 되는 상황에서도 코드를 되살릴 수 있게 함께 보관'],
            ].map(([k, v]) => (
              <tr key={k} className="border-t border-slate-100">
                <td className="px-2 py-1.5 font-semibold text-slate-700 whitespace-nowrap align-top">{k}</td>
                <td className="px-2 py-1.5 text-slate-600">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* 복원 방법 */}
      <div className="bg-amber-50 border border-amber-200 rounded p-5 text-sm text-amber-900">
        <h3 className="ui-subsection-title mb-2">🛟 되돌려야 할 때 — 복원 방법</h3>
        <p className="mb-2">
          복원은 <strong>이 화면이 아니라 서버에서</strong> 합니다. 서버가 통째로 손상된 상황에서는
          포털 자체가 뜨지 않아 화면을 쓸 수 없기 때문입니다.
        </p>
        <p className="mb-1">운영 서버에 접속해 아래 한 줄을 실행하면 됩니다.</p>
        <pre className="bg-amber-100 border border-amber-300 rounded px-3 py-2 my-2 overflow-x-auto text-xs text-amber-950">
          sudo bash /sales/app/ops/restore.sh
        </pre>
        <p className="text-xs">
          백업 목록을 보여주고 → 파일이 온전한지 검사하고 → <strong>무엇을 잃게 되는지 알려준 뒤</strong>{' '}
          두 번 확인받고 진행합니다. 확인을 통과하기 전까지는 서버에 아무 변화도 없습니다.
          예전 데이터도 따로 남겨 두므로 되돌리기가 가능합니다.
        </p>
        <p className="mt-2 text-xs text-amber-700">
          ※ 새 서버에 처음부터 복구하는 경우는 저장소{' '}
          <code className="bg-amber-100 px-1 rounded">ops/RUNBOOK.md</code> 5장의 준비 단계를 먼저 보세요.
          NAS 계정·서버 비밀번호는 <strong>백업 밖</strong>에 따로 보관해 두어야 합니다.
        </p>
      </div>
    </div>
  );
}

// 자동 백업(NAS) 현황 — 매일 새벽 1시 실행 결과
//   ⚠ 이 카드의 존재 이유: 2026-06-05~07-29 사이 백업이 55일간 매일 실패했는데
//     성공/실패를 아무 데도 안 알려서 아무도 몰랐다. '마지막 성공' 을 눈에 띄게 둔다.
function AutoBackupCard() {
  const q = useQuery({
    queryKey: ['admin', 'backups'],
    queryFn: adminApi.getBackupStatus,
    refetchOnWindowFocus: false,
  });

  if (q.isLoading) {
    return (
      <div className="bg-white border border-slate-200 rounded p-5 text-sm text-slate-500">
        자동 백업 현황을 불러오는 중…
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded p-5 text-sm text-slate-600">
        자동 백업 현황을 불러오지 못했습니다.
      </div>
    );
  }

  const d = q.data;
  // 기록이 아예 없는 것도 '이상' — 백업이 한 번도 안 돌았다는 뜻이므로 정상으로 보이면 안 된다.
  const ok = d.healthy;
  const box = ok ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300';
  const head = ok ? 'text-emerald-800' : 'text-red-800';

  return (
    <div className={`border-2 rounded p-5 ${box}`}>
      <div className="flex items-start gap-3">
        <span className="text-3xl leading-none">{ok ? '✅' : '⚠️'}</span>
        <div className="flex-1 min-w-0">
          <h3 className={`font-bold ${head}`}>
            {ok ? '자동 백업 정상' : d.lastSuccessAt ? '자동 백업 이상 — 확인 필요' : '자동 백업 기록 없음'}
          </h3>
          <p className="text-sm text-slate-700 mt-0.5">
            마지막 성공:{' '}
            <strong>{d.lastSuccessAt ? fmtWhen(d.lastSuccessAt) : '없음'}</strong>
            {d.hoursSinceLastSuccess != null && (
              <span className="text-slate-500"> ({fmtAgo(d.hoursSinceLastSuccess)})</span>
            )}
            {d.failStreak > 0 && (
              <span className="text-red-700 font-semibold"> · 그 뒤 실패 {d.failStreak}회</span>
            )}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {d.schedule} 실행 · 보관 위치 <code className="bg-white/70 px-1 rounded">{d.destRoot}</code>{' '}
            (NAS) · 최근 {d.retentionDays}일 보관
          </p>
        </div>
      </div>

      {d.rows.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs bg-white rounded border border-slate-200">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">실행 일시</th>
                <th className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">결과</th>
                <th className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">데이터</th>
                <th className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">설정·비밀정보</th>
                <th className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">소요</th>
                <th className="px-2 py-1.5 text-left font-semibold min-w-[14rem]">비고</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top">
                  <td className="px-2 py-1.5 whitespace-nowrap text-slate-700">
                    {fmtWhen(r.finishedAt ?? r.startedAt)}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <span
                      className={`px-1.5 py-0.5 rounded font-semibold ${
                        r.status === 'success'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {r.status === 'success' ? '성공' : '실패'}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums text-slate-600">
                    {fmtBytes(r.dbBytes)}
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums text-slate-600">
                    {fmtBytes(r.filesBytes)}
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap tabular-nums text-slate-600">
                    {r.durationSec == null ? '-' : `${r.durationSec}초`}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600 break-words whitespace-pre-line">
                    {r.message ?? (r.deletedCount > 0 ? `오래된 백업 ${r.deletedCount}건 정리` : '')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function fmtWhen(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function fmtAgo(hours: number): string {
  if (hours < 1) return '방금 전';
  if (hours < 24) return `${Math.floor(hours)}시간 전`;
  return `${Math.floor(hours / 24)}일 전`;
}

function fmtBytes(n: number): string {
  if (!n) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i >= 2 ? 1 : 0)} ${units[i]}`;
}

// 탭5 — 정합성 점검 (재고/매입 데이터 건강 상태)
function IntegrityTab() {
  const q = useQuery({
    queryKey: ['admin', 'integrity'],
    queryFn: adminApi.getIntegrity,
    staleTime: 10_000,
  });
  // 발견 건 클릭 시 상세 팝업 (key/label 보관)
  const [detail, setDetail] = useState<{ key: string; label: string } | null>(null);

  if (q.isLoading) {
    return <div className="text-center py-12 text-slate-400 text-sm">점검 중…</div>;
  }
  if (q.isError) {
    return (
      <div className="text-center py-12 text-red-500 text-sm">
        오류: {(q.error as Error).message}
      </div>
    );
  }
  const data = q.data!;
  const criticalIssues = data.critical.filter((c) => c.count > 0);
  const allClear = criticalIssues.length === 0;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* 종합 배너 */}
      <div
        className={`rounded p-4 border ${
          allClear
            ? 'bg-emerald-50 border-emerald-200'
            : 'bg-red-50 border-red-200'
        }`}
      >
        <div className="flex items-center gap-3">
          <span className="text-3xl">{allClear ? '✅' : '⚠️'}</span>
          <div>
            <div className={`font-bold ${allClear ? 'text-emerald-800' : 'text-red-800'}`}>
              {allClear ? '정합성 이상 없음' : `주의가 필요한 항목 ${criticalIssues.length}건`}
            </div>
            <div className={`text-xs ${allClear ? 'text-emerald-700' : 'text-red-700'}`}>
              {allClear
                ? '재고·매입 데이터에 위험 항목이 없습니다.'
                : '아래 위험 항목을 확인하세요.'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => q.refetch()}
            className="btn btn-secondary btn-sm ml-auto"
          >
            ↻ 다시 점검
          </button>
        </div>
      </div>

      {/* 위험 항목 — 0이어야 정상 */}
      <div>
        <h3 className="ui-subsection-title mb-2">위험 항목 (0이어야 정상)</h3>
        <div className="bg-white border border-slate-200 rounded overflow-hidden">
          {data.critical.map((c) => {
            const clickable = c.count > 0;
            return (
              <div
                key={c.key}
                onClick={clickable ? () => setDetail({ key: c.key, label: c.label }) : undefined}
                className={`flex items-center justify-between px-4 py-2.5 border-b border-slate-100 last:border-0 ${
                  clickable ? 'cursor-pointer hover:bg-red-50/60' : ''
                }`}
              >
                <span className="text-sm text-slate-700">
                  {c.label}
                  {clickable && <span className="ml-1.5 text-xs text-blue-600">상세 ▸</span>}
                </span>
                <span
                  className={`text-sm font-bold px-2 py-0.5 rounded ${
                    c.count > 0
                      ? 'bg-red-100 text-red-700'
                      : 'bg-emerald-100 text-emerald-700'
                  }`}
                >
                  {c.count > 0 ? `⚠ ${c.count}건` : '✓ 0'}
                </span>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-1.5">발견된 항목을 클릭하면 어떤 건인지 상세 목록을 볼 수 있습니다.</p>
      </div>

      {/* 참고 항목 — 0 아니어도 무해 */}
      <div>
        <h3 className="ui-subsection-title mb-2">참고 (0 아니어도 재고에 무해)</h3>
        <div className="bg-white border border-slate-200 rounded overflow-hidden">
          {data.info.map((c) => {
            const clickable = c.count > 0;
            return (
              <div
                key={c.key}
                onClick={clickable ? () => setDetail({ key: c.key, label: c.label }) : undefined}
                className={`flex items-center justify-between px-4 py-2.5 border-b border-slate-100 last:border-0 ${
                  clickable ? 'cursor-pointer hover:bg-slate-50' : ''
                }`}
              >
                <span className="text-sm text-slate-500">
                  {c.label}
                  {clickable && <span className="ml-1.5 text-xs text-blue-600">상세 ▸</span>}
                </span>
                <span className="text-sm font-medium text-slate-500">{c.count}건</span>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-1.5">
          참고 항목은 과거 적재분·서비스 품목 등에서 생기는 표시상 잔재로, 재고 수량 계산에는 영향을 주지 않습니다.
        </p>
      </div>

      {detail && (
        <IntegrityDetailModal
          detailKey={detail.key}
          label={detail.label}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

// 정합성 점검 — 발견 건 상세 팝업
function IntegrityDetailModal({
  detailKey,
  label,
  onClose,
}: {
  detailKey: string;
  label: string;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ['admin', 'integrity', 'detail', detailKey],
    queryFn: () => adminApi.getIntegrityDetail(detailKey),
    staleTime: 10_000,
  });
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4"
    >
      <div
        className="bg-white rounded shadow-xl w-full max-w-3xl my-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t">
          <h3 className="ui-subsection-title">🔎 {label} — 상세</h3>
          <button
            type="button"
            onClick={onClose}
            className="ui-close"
          >
            ×
          </button>
        </div>
        <div className="p-4">
          {q.isLoading ? (
            <div className="text-center py-8 text-slate-400 text-sm"><LoadingLines /></div>
          ) : q.isError ? (
            <div className="text-center py-8 text-red-500 text-sm">오류: {(q.error as Error).message}</div>
          ) : !q.data || q.data.rows.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">표시할 상세 내역이 없습니다.</div>
          ) : (
            <>
              <div className="text-xs text-slate-500 mb-2">{q.data.rows.length}건</div>
              <div className="overflow-x-auto border border-slate-200 rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      {q.data.columns.map((col) => (
                        <th key={col.key} className="px-2 py-1.5 text-left text-slate-600 font-medium whitespace-nowrap">
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.rows.map((row, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        {q.data!.columns.map((col) => {
                          const v = row[col.key];
                          return (
                            <td key={col.key} className="px-2 py-1.5 text-slate-700 whitespace-nowrap">
                              {v === null || v === undefined || v === '' ? '-' : String(v)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {q.data.rows.length >= 500 && (
                <p className="text-xs text-amber-600 mt-2">최대 500건까지만 표시합니다.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// 정렬 가능한 표 헤더 셀 — 클릭 시 토글, 활성 컬럼은 화살표 표시
function SortableTh<K extends string>({
  label,
  sortKey,
  currentKey,
  currentDir,
  onClick,
  align,
}: {
  label: string;
  sortKey: K;
  currentKey: K;
  currentDir: 'asc' | 'desc';
  onClick: (k: K) => void;
  align: 'left' | 'center' | 'right';
}) {
  const active = currentKey === sortKey;
  const arrow = active ? (currentDir === 'asc' ? '▲' : '▼') : '↕';
  const alignClass =
    align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
  return (
    <th
      onClick={() => onClick(sortKey)}
      className={`${alignClass} cursor-pointer select-none hover:bg-slate-100 ${
        active ? 'text-blue-700 font-semibold' : ''
      }`}
      title="클릭하여 정렬 (다시 클릭하면 역순)"
    >
      {label}
      <span className={`ml-1 text-[11px] ${active ? 'text-blue-600' : 'text-slate-300'}`}>
        {arrow}
      </span>
    </th>
  );
}

// ========================================================================
// 탭1 — 계정 관리
// ========================================================================
type SortKey =
  | 'userId'
  | 'empName'
  | 'empTitle'
  | 'empDepartment'
  | 'role'
  | 'isActive'
  | 'securityLevel'
  | 'lastLoginAt';
type SortDir = 'asc' | 'desc';

function AccountsTab() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const sessionUserId = user?.userId;
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminUserRow | null>(null);
  const [resetting, setResetting] = useState<AdminUserRow | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('userId');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const query = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => adminApi.listUsers(),
    staleTime: 30_000,
  });

  // 활성 토글 — 한 클릭으로 비활성화/활성화
  const toggleMut = useMutation({
    mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) =>
      adminApi.updateUser(userId, { isActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (e: Error) => {
      alert(`상태 변경 실패: ${e.message}`);
    },
  });

  // 2단계 인증 초기화 (v2.231.0 · F-11) — 휴대폰을 잃고 복구 코드도 없을 때 관리자가 대신 끈다
  const reset2faMut = useMutation({
    mutationFn: (userId: string) => adminApi.resetUserTwoFactor(userId),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      alert(`2단계 인증을 초기화했습니다 (신뢰 기기 ${r.devicesRemoved}대 해제).\n다음 로그인은 비밀번호만으로 됩니다 — 본인이 마이페이지에서 다시 등록하게 안내해 주세요.`);
    },
    onError: (e: Error) => {
      alert(`2단계 인증 초기화 실패: ${e.message}`);
    },
  });

  function handleReset2fa(u: AdminUserRow) {
    const label = u.empName ?? u.userId;
    const msg = `[${label}] 의 2단계 인증을 초기화하시겠습니까?\n\n` +
      `· OTP 앱 연결·복구 코드·신뢰 기기가 모두 지워집니다.\n` +
      `· 다음 로그인은 비밀번호만으로 되고, 본인이 마이페이지에서 다시 등록해야 합니다.\n\n` +
      `휴대폰 분실처럼 본인이 직접 끌 수 없을 때만 쓰세요.`;
    if (!window.confirm(msg)) return;
    reset2faMut.mutate(u.userId);
  }

  function handleToggleActive(u: AdminUserRow) {
    const willActivate = !u.isActive;
    const verb = willActivate ? '활성화' : '비활성화';
    const label = u.empName ?? u.userId;
    const msg = willActivate
      ? `[${label}] 계정을 활성화하시겠습니까?\n로그인이 다시 가능해집니다.`
      : `[${label}] 계정을 비활성화하시겠습니까?\n로그인이 즉시 차단됩니다 (이력은 보존).`;
    if (!window.confirm(msg)) return;
    toggleMut.mutate({ userId: u.userId, isActive: willActivate });
    void verb; // for clarity
  }

  const users = query.data?.users ?? [];
  const counts = useMemo(() => {
    const c = { admin: 0, mgmt: 0, sales: 0, viewer: 0, active: 0, inactive: 0 };
    for (const u of users) {
      c[u.role] += 1;
      if (u.isActive) c.active += 1;
      else c.inactive += 1;
    }
    return c;
  }, [users]);

  const sortedUsers = useMemo(() => {
    const copy = [...users];
    copy.sort((a, b) => {
      const av = a[sortKey] as string | number | boolean | null | undefined;
      const bv = b[sortKey] as string | number | boolean | null | undefined;
      // null/undefined 는 항상 끝으로
      if ((av === null || av === undefined) && (bv === null || bv === undefined)) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      let cmp: number;
      if (typeof av === 'boolean' && typeof bv === 'boolean') {
        // 활성 먼저 (true → 위)
        cmp = av === bv ? 0 : av ? -1 : 1;
      } else if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else {
        cmp = String(av).localeCompare(String(bv), 'ko');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [users, sortKey, sortDir]);

  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-slate-600">
          총 <strong>{users.length}</strong>명 (활성 {counts.active} · 비활성 {counts.inactive}) ·{' '}
          역할 분포: admin {counts.admin} · mgmt {counts.mgmt} · sales {counts.sales} · viewer {counts.viewer}
        </p>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="btn btn-primary"
        >
          + 계정 추가
        </button>
      </div>

      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        <table className="data-table w-full text-[13px]">
          <thead>
            <tr>
              <SortableTh label="ID" sortKey="userId" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort} align="left" />
              <SortableTh label="이름" sortKey="empName" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort} align="left" />
              <SortableTh label="직급" sortKey="empTitle" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort} align="left" />
              <SortableTh label="부서" sortKey="empDepartment" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort} align="left" />
              <SortableTh label="역할" sortKey="role" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort} align="center" />
              <SortableTh label="상태" sortKey="isActive" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort} align="center" />
              <SortableTh label="보안" sortKey="securityLevel" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort} align="center" />
              <SortableTh label="최종 로그인" sortKey="lastLoginAt" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort} align="left" />
              <th className="text-center w-44">액션</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr>
                <td colSpan={9} className="text-center py-8 text-slate-400"><LoadingLines /></td>
              </tr>
            )}
            {query.isError && (
              <tr>
                <td colSpan={9} className="text-center py-8 text-red-500">
                  오류: {(query.error as Error).message}
                </td>
              </tr>
            )}
            {query.data && users.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center py-8 text-slate-400">
                  등록된 계정이 없습니다.
                </td>
              </tr>
            )}
            {sortedUsers.map((u) => {
              const isSelf = u.userId === sessionUserId;
              return (
                <tr key={u.userId} className={u.isActive ? '' : 'bg-slate-50'}>
                  <td className="font-mono text-xs">
                    {u.userId}
                    {isSelf && (
                      <span className="ml-1 text-[11px] text-blue-600">
                        (본인)
                      </span>
                    )}
                  </td>
                  {/* 이름 + 휴대폰(v2.211.0)을 한 칸에 두 줄로 — 부서·팀 칸과 같은 이유(새 칼럼은 표를 넓힌다).
                      휴대폰은 메일 꼬리말 M · 견적서 담당자 연락처라 관리자가 비어 있는 사람을 한눈에 봐야 한다. */}
                  <td className="font-medium">
                    {u.empName ?? '-'}
                    {u.empMobile ? (
                      <span className="block text-xs font-normal text-slate-500 whitespace-nowrap">{u.empMobile}</span>
                    ) : null}
                  </td>
                  <td className="text-slate-600">{u.empTitle ?? '-'}</td>
                  {/* 부서 + 팀을 한 칸에 두 줄로 — 새 칼럼을 만들면 표가 넓어진다.
                      글자 크기는 같게 두고 색으로만 위계를 준다. */}
                  <td className="text-slate-600">
                    {u.empDepartment ?? '-'}
                    {u.empTeam ? (
                      <span className="block text-slate-400">{u.empTeam}</span>
                    ) : null}
                  </td>
                  <td className="text-center">
                    <span className={ROLE_BADGE[u.role]}>{u.role}</span>
                  </td>
                  <td className="text-center">
                    {u.isActive ? (
                      <span className="text-emerald-600 text-xs">● 활성</span>
                    ) : (
                      <span className="text-slate-500 text-xs">○ 비활성</span>
                    )}
                  </td>
                  <td className="text-center text-xs text-slate-500">
                    {u.securityLevel}
                    {/* 2단계 인증 초기화 (v2.231.0 · F-11) — 새 칸을 만들면 표가 넓어지므로 보안 칸 아래 줄에 */}
                    {!isSelf && u.securityLevel !== 'basic' && (
                      <button
                        type="button"
                        onClick={() => handleReset2fa(u)}
                        disabled={reset2faMut.isPending}
                        title="휴대폰 분실 등 — OTP·복구 코드·신뢰 기기를 지우고 비밀번호만으로 로그인하게 합니다"
                        className="block mx-auto text-xs text-red-600 hover:underline disabled:opacity-50 whitespace-nowrap"
                      >
                        초기화
                      </button>
                    )}
                  </td>
                  <td className="text-slate-500 text-xs">
                    {fmtDateTime(u.lastLoginAt)}
                  </td>
                  <td className="text-center text-xs whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setEditing(u)}
                      className="text-blue-600 hover:underline"
                    >
                      수정
                    </button>
                    <span className="text-slate-300 mx-1">|</span>
                    {isSelf ? (
                      <span
                        title="본인 비밀번호는 마이페이지에서 변경"
                        className="text-slate-300 cursor-not-allowed"
                      >
                        비번재설정
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setResetting(u)}
                        className="text-slate-600 hover:underline"
                      >
                        비번재설정
                      </button>
                    )}
                    {!isSelf && (
                      <>
                        <span className="text-slate-300 mx-1">|</span>
                        <button
                          type="button"
                          onClick={() => handleToggleActive(u)}
                          disabled={toggleMut.isPending}
                          title={
                            u.isActive
                              ? '비활성화 — 로그인 차단 (이력 보존)'
                              : '활성화 — 로그인 다시 허용'
                          }
                          className={
                            u.isActive
                              ? 'text-red-600 hover:underline disabled:opacity-50'
                              : 'text-emerald-600 hover:underline disabled:opacity-50'
                          }
                        >
                          {u.isActive ? '비활성화' : '활성화'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {creating && (
        <UserFormModal
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={() => setCreating(false)}
        />
      )}
      {editing && (
        <UserFormModal
          mode="edit"
          initial={editing}
          isSelf={editing.userId === sessionUserId}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}
      {resetting && (
        <ResetPasswordModal
          userId={resetting.userId}
          userName={resetting.empName}
          onClose={() => setResetting(null)}
          onDone={() => setResetting(null)}
        />
      )}
    </>
  );
}

// ========================================================================
// 탭2 — 메뉴 권한 설정 (정적 매트릭스)
// ========================================================================
const PERMISSION_MATRIX: {
  menu: string;
  admin: boolean;
  mgmt: boolean;
  sales: boolean;
  viewer: boolean;
}[] = [
  // 2026-07-19 메뉴 재구성(v1.7.4~v1.7.7) 반영 — 사이드바 4그룹 기준.
  { menu: '대시보드',                            admin: true, mgmt: true,  sales: true,  viewer: true  },
  { menu: '영업진행 — 조회',                     admin: true, mgmt: true,  sales: true,  viewer: true  },
  { menu: '영업진행 — 등록/수정',                admin: true, mgmt: true,  sales: true,  viewer: false },
  { menu: '영업진행 — 매입금액·이익 표시',       admin: true, mgmt: true,  sales: true,  viewer: false },
  { menu: '영업건 삭제 · 반품 처리',             admin: true, mgmt: true,  sales: false, viewer: false },
  // v2.251.0: 유지보수 화면은 viewer 도 — 금액은 「***」(서버가 가림)
  { menu: '유지보수 — 조회 (계약·기간·장비·현장 연락처·발행 여부)', admin: true, mgmt: true,  sales: true,  viewer: true  },
  { menu: '유지보수 — 금액 · 엑셀 · 계약 문서 · 예산 산정근거 · 등록/수정', admin: true, mgmt: true, sales: true, viewer: false },
  { menu: '비상벨',                              admin: true, mgmt: true,  sales: true,  viewer: false },
  { menu: '재고관리 — 조회',                     admin: true, mgmt: true,  sales: true,  viewer: true  },
  { menu: '재고관리 — 품목 추가/수정',           admin: true, mgmt: true,  sales: true,  viewer: false },
  { menu: '재고관리 — 출고 승인/취소',           admin: true, mgmt: true,  sales: false, viewer: false },
  { menu: '재고관리 — 임대 회수/마무리',         admin: true, mgmt: true,  sales: false, viewer: false },
  { menu: '매입관리 — 조회',                     admin: true, mgmt: true,  sales: true,  viewer: true  },
  { menu: '매입관리 — 단가/금액 표시',           admin: true, mgmt: true,  sales: true,  viewer: false },
  { menu: '매입관리 — 등록/수정/삭제',           admin: true, mgmt: true,  sales: true,  viewer: false },
  { menu: '매입관리 — 국세청 업로드/매칭 점검',  admin: true, mgmt: true,  sales: false, viewer: false },
  { menu: '내 결재함 (판보 결재)',               admin: true, mgmt: true,  sales: true,  viewer: false },
  { menu: '거래 문서함 — 영업 문서 탭(견적서·발주서·관급내역서·정산서·고객발주서·매입견적서)', admin: true, mgmt: true, sales: true, viewer: false },
  // v2.290.0: 세금계산서 탭이 거래 문서함에서 영업진행으로
  { menu: '영업진행 › 세금계산서 탭(발행요청 이력 · 수신·처리 내역)', admin: true, mgmt: true, sales: true, viewer: false },
  { menu: '사내결재 문서함 (지출결재·카드내역·휴일근무·월간보고서·회의록·분기 실사·지출 현황 — 지출 내용은 작성자·결재선만)', admin: true, mgmt: true, sales: true, viewer: true },
  { menu: '판매보고서 작성 · 영업진행 › 판매보고서 탭(확정본 열람)', admin: true, mgmt: true, sales: true, viewer: false },
  // v2.250.0: 메일을 전 직원에게 — 메일함은 각자 연결한 본인 것만 · 금액 문서 첨부는 영업 이상(서버 doc-source)
  { menu: '메일 (받은 메일·보내기·연결·꼬리말 — 본인 웍스 메일함)', admin: true, mgmt: true, sales: true, viewer: true },
  { menu: '메일 — 견적서·발주서·거래명세서·비상벨 납기현황 첨부', admin: true, mgmt: true, sales: true, viewer: false },
  { menu: '문서 — 타인 작성 수정·삭제',          admin: true, mgmt: true,  sales: true,  viewer: false },
  { menu: '매출 분석 · 주요항목 가격표',         admin: true, mgmt: true,  sales: true,  viewer: false },
  { menu: '나라장터쇼핑몰 현황 — 조회',          admin: true, mgmt: true,  sales: true,  viewer: true  },
  { menu: '나라장터 — 편집·문서생성·BOM',        admin: true, mgmt: true,  sales: true,  viewer: false },
  // v2.318.0: 거래처 마스터 조회(거래처 360)를 영업에게 — 쓰기와 통장사본은 그대로 admin·mgmt
  { menu: '거래처 — 거래처 마스터 조회 · 거래처 360(연결된 영업·매입·유지보수·딜·메모·문서)', admin: true, mgmt: true, sales: true, viewer: false },
  { menu: '거래처 — 등록·수정·삭제 · 사업자등록증 첨부 · 통장사본', admin: true, mgmt: true, sales: false, viewer: false },
  { menu: '수출입 (수입 오더·원가)',             admin: true, mgmt: true,  sales: false, viewer: false },
  { menu: '변경 이력 — 조회 (금액은 권한별 마스킹)', admin: true, mgmt: true, sales: true, viewer: true },
  { menu: '변경 이력 — 되돌리기',                admin: true, mgmt: false, sales: false, viewer: false },
  { menu: '시스템 관리',                         admin: true, mgmt: false, sales: false, viewer: false },
];

function PermissionsTab() {
  return (
    <div>
      <p className="text-sm text-slate-600 mb-4">
        역할별 메뉴·기능 접근 권한 안내 (2026-07-19 메뉴 재구성 기준). 정책으로 고정 —
        여기서 바꾸는 것이 아니라 현재 규칙을 보여주는 표입니다.
      </p>
      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-hidden">
        <table className="data-table w-full text-[13px]">
          <thead>
            <tr>
              <th className="text-left !bg-slate-700 !text-white">메뉴 / 기능</th>
              <th className="text-center w-28 !bg-slate-700 !text-white">admin</th>
              <th className="text-center w-28 !bg-slate-700 !text-white">mgmt</th>
              <th className="text-center w-28 !bg-slate-700 !text-white">sales</th>
              <th className="text-center w-28 !bg-slate-700 !text-white">viewer</th>
            </tr>
          </thead>
          <tbody>
            {PERMISSION_MATRIX.map((p) => (
              <tr key={p.menu}>
                <td>{p.menu}</td>
                <td className="text-center">
                  {p.admin ? (
                    <span className="text-emerald-600 text-lg">✓</span>
                  ) : (
                    <span className="text-slate-300 text-lg">✗</span>
                  )}
                </td>
                <td className="text-center">
                  {p.mgmt ? (
                    <span className="text-emerald-600 text-lg">✓</span>
                  ) : (
                    <span className="text-slate-300 text-lg">✗</span>
                  )}
                </td>
                <td className="text-center">
                  {p.sales ? (
                    <span className="text-emerald-600 text-lg">✓</span>
                  ) : (
                    <span className="text-slate-300 text-lg">✗</span>
                  )}
                </td>
                <td className="text-center">
                  {p.viewer ? (
                    <span className="text-emerald-600 text-lg">✓</span>
                  ) : (
                    <span className="text-slate-300 text-lg">✗</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-slate-500 grid grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded p-3">
          <strong className="text-slate-900">admin</strong>
          <br />
          모든 메뉴 + 시스템 관리. 계정 CRUD + 비번 재설정.
        </div>
        <div className="bg-white border border-slate-200 rounded p-3">
          <strong className="text-slate-900">mgmt (관리자급)</strong>
          <br />
          sales 권한 + 영업건 삭제 + 출고 승인/취소 + 임대 회수 + 문서 타인작성 수정·삭제.
        </div>
        <div className="bg-white border border-slate-200 rounded p-3">
          <strong className="text-slate-900">sales (영업부)</strong>
          <br />
          매입가·이익률 노출. 매입관리·매출분석·판보 작성. 영업건 삭제·출고 승인 X.
        </div>
        <div className="bg-white border border-slate-200 rounded p-3">
          <strong className="text-slate-900">viewer (일반직원)</strong>
          <br />
          영업진행(매출만)·재고·매입목록만 열람. 매입가/이익은 마스킹.
        </div>
      </div>
    </div>
  );
}
